import {GENERATORS, handleAction, handleSubState, Actions} from 'uix/actions'
import {dict, filterItems, has, size, keys, mapItems, get, isNil, groupBy} from 'ldash'
import log from 'uix/logging'
import {Sel} from 'uix/registries'
import {persistReducer} from 'uix/persist'
import {reduceMergeReducers} from 'uix/utils/reducers'

import {
  setTreeChildren,
  clearTree,
  setAvailableTypes,
  setAvailableStates,
  setActiveTreeNode,
  setTreeChildrenLoading,
  // setTreeChildrenActive,
  getTreeDetails,
  setTreeDetails,
  setTreeDetailsErrors,
  switchTabGroup,
  switchTopTab,
  setDescendants,
  setTreeNodeData,
  setTreeNodesData,
  pinTreeNode,
  clearTreePinned,
  setDelivererYears,
  setDelivererYearMds,
} from './actions'

let TREE = {
  dataVersion: 0, // increase will rebuild tree structure (by applying rules)
  activeNode: null, // track focused node globally
  // build nodes from these data packages
  childData: {}, // map idContext/otids to child int otids or slimlink strings (NTYPE.VLTYPE)
  childDataControl: {}, // map idContext/otids to getChildren action payload (for refresh)
  childDataOverflow: {}, // if massive amounts of slimlinks detected, store stuff from childData in childDataOverflow

  byId: {}, // map otids to basic node info (OPR_Tree)
  detailsById: {}, // map otids to custom node info (haschildren, pdf or ocr indicators)
  descendantData: {}, // key: node.id, value: list of descendant nodes (with parent info)
  availableTypes: {}, // NTYPE.DOMAIN_BYTYPE
  availableStates: {}, // NTYPE.DOMAIN_BYSTATE
  deliverers: {}, // NTYPE.DELIVERER_YEAR
  // loading state
  loading: {},
  detailsLoadErrors: {}, // stop trying to resolve load errors
  loadingDetails: {},
  // childrenActive: {}, // given a parent otid, track active getChildren calls
  // tab related - does probably not belong here
  chosenTabs: {},
  chosenGroups: {},
  chosenTopTabs: {},
  chosenMinorTabGroupTabs: {},
  // custom stuff
  pinned: {},
}

let reducers = [
  handleAction(clearTree, tree => TREE, TREE),
  // merge payload into tree.childData and tree.byId
  // update tree.dataVersion
  handleAction(
    setTreeChildren,
    ({childData, childDataControl, childDataOverflow, ...tree}, {payload, meta}) => {
      let {data, otid, idContext, refreshParams} = payload
      let {treeDataVersion} = meta

      let id = !otid ? 0 : otid

      // prevent massive amounts of node
      const OVERFLOW_TRIGGER = 2000
      const OVERFLOW_LIMIT = 1000
      if (data.length > OVERFLOW_TRIGGER) {
        let {linked = [], unlinked = []} = groupBy(data, e => (e.slimlink ? 'linked' : 'unlinked'))
        data = [...unlinked, ...linked.slice(0, OVERFLOW_LIMIT)]

        let overflowContextData = {
          ...childDataOverflow[idContext],
          [id]: linked.slice(OVERFLOW_LIMIT).map(e => e.slimlink),
        }
        log.info('dumping %o into tree overflow data', linked.slice(OVERFLOW_LIMIT).length)
        childDataOverflow = {...childDataOverflow, [idContext]: overflowContextData}
      }

      let contextData = {...childData[idContext], [id]: data.map(e => e.slimlink || e.otid)}
      childData = {...childData, [idContext]: contextData}

      // for refresh
      let controlData = {...childDataControl[idContext], [id]: refreshParams}
      childDataControl = {...childDataControl, [idContext]: controlData}

      // e.slimlink has the form 'sl123_456'
      let byOtid = dict(data.map(e => [e.slimlink || e.otid, e]))
      let byId = {...tree.byId, ...byOtid}

      return {
        ...tree,
        dataVersion: treeDataVersion,
        childData,
        childDataControl,
        childDataOverflow,
        byId,
      }
    }
  ),

  handleAction(setTreeNodeData, (tree, {payload}) => {
    let byId = {...tree.byId, [payload.otid]: payload}
    return {...tree, byId}
  }),

  handleAction(setTreeNodesData, (tree, {payload, meta}) => {
    let {treeDataVersion} = meta
    let {data} = payload
    let byOtid = dict(data.map(e => [e.otid, e]))
    let byId = {...tree.byId, ...byOtid}
    return {...tree, byId, dataVersion: treeDataVersion}
  }),

  handleAction('SET_TREE_RECORDS', (tree, {payload, meta}) => {
    /* handle result from /api/treeFields (also triggered by ERecordDataChanged)
    - refresh some values in tree.byId (is this ok, update more than caption?)
    */
    let {data, fields} = payload
    let {byId} = tree
    if (!fields.includes('caption')) {
      return tree
    }
    // we copy only caption from data.db.byId to data.tree.byId
    // we will probably copy more later
    let candidates = dict(data.filter(r => r.caption !== undefined).map(r => [r.otid, r]))
    let modified = false
    byId = mapItems(byId, (otid, r) => {
      if (candidates[otid]) {
        modified = true
        r = {...r, ...candidates[otid]}
      }
      return [otid, r]
    })
    // no tree update
    if (!modified) {
      return tree
    }
    byId = dict(byId)

    return {...tree, dataVersion: GENERATORS.treeDataVersion(), byId}
  }),

  handleAction(setDescendants, ({descendantData, ...tree}, {payload}) => {
    let {node, data, typeNames} = payload
    let byOtid = dict(data.map(e => [e.otid, e]))
    let otids = data.map(e => e.otid)
    let byId = {...tree.byId, ...byOtid}
    let byTypeNames = descendantData[node.id] || {}
    byTypeNames = {...byTypeNames, [typeNames.join(',')]: otids}
    descendantData = {...descendantData, [node.id]: byTypeNames}
    return {...tree, descendantData, byId}
  }),

  handleAction(setAvailableTypes, (tree, {payload, meta}) => ({
    ...tree,
    dataVersion: meta.treeDataVersion,
    availableTypes: {...tree.availableTypes, [payload.dname]: payload.data},
  })),

  handleAction(setAvailableStates, ({availableStates, ...tree}, {payload, meta}) => ({
    ...tree,
    dataVersion: meta.treeDataVersion,
    availableStates: {
      ...availableStates,
      [payload.dname]: {...get(availableStates, payload.dname, {}), ...payload.data},
    },
  })),
  handleAction('SET_STATES_CFG', tree => ({...tree, dataVersion: GENERATORS.treeDataVersion()})),
  //~ handleAction('TREE_RECALC', tree => ({...tree, dataVersion: GENERATORS.treeDataVersion()})),
  persistReducer(
    reduceMergeReducers(
      handleAction(pinTreeNode, ({pinned, ...tree}, {payload}) => {
        return {
          ...tree,
          pinned: {...pinned, [payload.otid]: payload.isPinned},
          dataVersion: GENERATORS.treeDataVersion(),
        }
      }),
      handleAction(clearTreePinned, tree => ({
        ...tree,
        pinned: {},
        dataVersion: GENERATORS.treeDataVersion(),
      }))
    ),
    {key: 'uix_tree_pinned', whitelist: ['pinned']}
  ),
  // receiving server-side persisted values
  handleAction(Actions.initUiState, (tree, {payload}) =>
    payload.uistate.pinned
      ? {...tree, pinned: dict(payload.uistate.pinned.map(otid => [otid, true]))}
      : tree
  ),
  handleAction(setActiveTreeNode, (tree, {payload}) => {
    // redux store should not contain mutable data
    // pop children + shallow node copy (there is a inplace "expanded" mutation for tree nodes in SortableTree)
    let {children, ...activeNode} = payload.node
    return {...tree, activeNode}
  }),

  handleAction(setTreeChildrenLoading, (tree, {payload}) => ({
    ...tree,
    loading: {...tree.loading, [payload.hash]: payload.value},
  })),
  //~ handleAction(setTreeChildrenActive, (tree, {payload}) => ({
  //~ ...tree,
  //~ childrenActive: {
  //~ ...tree.childrenActive,
  //~ [payload.idContext]: {
  //~ ...(tree.childrenActive[payload.idContext] || {}),
  //~ [payload.otid]: payload.getChildrenParams,
  //~ },
  //~ },
  //~ })),

  handleAction(getTreeDetails, (tree, {payload}) => ({
    ...tree,
    loadingDetails: {...tree.loadingDetails, [payload.otid]: payload.datatype},
  })),
  handleAction(setTreeDetailsErrors, ({detailsLoadErrors, loadingDetails, ...tree}, {payload}) => ({
    ...tree,
    detailsLoadErrors: {...detailsLoadErrors, ...payload.loadFailures},
    loadingDetails: dict(filterItems(loadingDetails, otid => isNil(payload.loadFailures[otid]))),
  })),

  // merge payload into tree.detailsById
  handleAction(setTreeDetails, (tree, action) => {
    let {data} = action.payload
    let byOtid = dict(data.map(e => [e.otid, e]))

    // remove from loading set
    let loadingDetails = dict(filterItems(tree.loadingDetails, (otid, v) => !has(byOtid, otid)))

    let detailsById = {...tree.detailsById, ...byOtid}

    return {...tree, detailsById, loadingDetails}
  }),
  handleAction(setDelivererYears, ({childData, deliverers, ...tree}, {payload}) => {
    /* set data for children of datatype 14 nodes
    derived from setTreeChildren
    */
    let {data, otid, idContext} = payload

    // XXX: vltype node (dt14) will assume it's now loaded
    let contextData = {...childData[idContext], [otid]: []}
    childData = {...childData, [idContext]: contextData}
    deliverers = {...deliverers, [otid]: dict(data.years.map(y => [y, null]))}
    return {...tree, childData, deliverers, dataVersion: GENERATORS.treeDataVersion()}
  }),
  handleAction(setDelivererYearMds, ({deliverers, byId, ...tree}, {payload}) => {
    /* set data for children of dt14/year nodes
     */
    let {md_nodes, otid, year} = payload
    let years = deliverers[otid] || {}

    byId = {...byId, ...dict(md_nodes.map(n => [n.otid, n]))}
    deliverers = {...deliverers, [otid]: {...years, [year]: md_nodes.map(n => n.otid)}}
    return {...tree, byId, deliverers, dataVersion: GENERATORS.treeDataVersion()}
  }),
]

export const data = [
  handleSubState('tree', reducers),

  handleAction(switchTabGroup, (data, {payload}) => {
    let {nodeType, group, classname, tabname} = payload
    let {chosenTabs, chosenGroups} = data.tree
    chosenTabs = {...chosenTabs, [nodeType]: [classname, tabname]}
    chosenGroups = {...chosenGroups, [nodeType]: group}
    return {...data, tree: {...data.tree, chosenGroups, chosenTabs}}
  }),

  handleAction(switchTopTab, (data, {payload}) => {
    let {chosenTopTabs, chosenMinorTabGroupTabs, activeNode, chosenTabs} = data.tree
    if (!activeNode) {
      log.warn('no activeNode for SWITCH_TOP_TAB')
      return data
    }
    let {classname, tabname, group, minorTabGroup} = payload
    let ntype = activeNode.nodeType
    chosenTopTabs = nextChosenTopTabs(classname, tabname, ntype, group, chosenTopTabs)
    chosenTabs = {...chosenTabs, [ntype]: [classname, tabname]}
    let tree = {...data.tree, chosenTopTabs, chosenTabs}

    if (minorTabGroup) {
      let k = `${group}/${minorTabGroup}`
      tree = {
        ...tree,
        chosenMinorTabGroupTabs: {...chosenMinorTabGroupTabs, [k]: [classname, tabname]},
      }
    }

    return {...data, tree}
  }),
]

let nextChosenTopTabs = (classname, tabname, ntype, group, chosenTopTabs) => {
  let chosenTopTabsForNodeType = chosenTopTabs[ntype] || {}
  chosenTopTabsForNodeType = {...chosenTopTabsForNodeType, [group]: [classname, tabname]}
  return (chosenTopTabs = {...chosenTopTabs, [ntype]: chosenTopTabsForNodeType})
}

export const root = [
  // initialize chosenTopTabs after app startup
  handleAction(setActiveTreeNode, (root, {payload}) => {
    // init only once
    if (size(root.data.tree.chosenTabs) > 0) return root

    let {classname, tabname} = root.meta.request
    let ntype = payload.node.nodeType
    let classCfg = Sel.classCfg(root)
    let group = classCfg.tabs[tabname].tabGroup || classCfg.tabGroup
    let chosenTopTabs = nextChosenTopTabs(classname, tabname, ntype, group, {})
    let chosenTabs = {[ntype]: [classname, tabname]}
    let tree = {...root.data.tree, chosenTopTabs, chosenTabs}

    let minorTabGroup = classCfg.tabs[tabname].minorTabGroup
    if (group && minorTabGroup) {
      let k = `${group}/${minorTabGroup}`
      tree = {...tree, chosenMinorTabGroupTabs: {[k]: [classname, tabname]}}
    }

    let data = {...root.data, tree}
    return {...root, data}
  }),
]
